/**
 * MeetBits — co-located controls for the "Meet your genie" onboarding step.
 *
 * VoiceOption: a tap-to-preview voice card (name + tag + play). Selecting it
 * raises the selection ring and plays the voice's REAL preview audio, which the
 * page owns so only one preview ever plays at a time.
 * TraitChip: a toggleable personality chip (shadcn Toggle), brand-tinted when on.
 *
 * Brand law: genie-* tokens only; no raw hex; Lucide inline icons only.
 */
import { Check, Loader2, Play } from "lucide-react";
import { Toggle } from "@/components/ui/toggle";

export interface VoicePreset {
  name: string;
  /** Short descriptor, e.g. "Warm / NZ" */
  tag: string;
}

interface VoiceOptionProps {
  preset: VoicePreset;
  selected: boolean;
  /** True while this voice's real preview audio is playing. */
  playing?: boolean;
  onSelect: () => void;
}

export const VoiceOption = ({ preset, selected, playing, onSelect }: VoiceOptionProps) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`group h-11 pl-1.5 pr-3.5 rounded-full flex items-center gap-2.5 transition-all ${
        selected
          ? "bg-genie-primary/10 border border-genie-primary/40"
          : "bg-white border border-genie-border hover:border-genie-primary/30"
      }`}
    >
      <span
        className={`w-[30px] h-[30px] rounded-full flex items-center justify-center transition-colors ${
          selected ? "bg-genie-primary" : "bg-[#F9FAFC] group-hover:bg-genie-primary/10"
        }`}
      >
        {playing ? (
          <Loader2
            className={`w-3 h-3 animate-spin ${selected ? "text-white" : "text-genie-primary"}`}
            strokeWidth={2}
          />
        ) : (
          <Play
            className={`w-3 h-3 ${selected ? "text-white" : "text-[#64748B]"}`}
            strokeWidth={1.8}
            fill="currentColor"
          />
        )}
      </span>
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[13px] font-semibold text-[#222B45]">{preset.name}</span>
        <span className="text-[11px] text-gray-500">{preset.tag}</span>
      </span>
      {selected && <Check className="w-[15px] h-[15px] text-genie-primary ml-0.5" strokeWidth={1.8} />}
    </button>
  );
};

interface TraitChipProps {
  label: string;
  on: boolean;
  onToggle: () => void;
}

export const TraitChip = ({ label, on, onToggle }: TraitChipProps) => {
  return (
    <Toggle
      pressed={on}
      onPressedChange={onToggle}
      className={`h-8 px-3 rounded-full border text-[12.5px] font-semibold gap-1.5 data-[state=on]:bg-genie-secondary/10 ${
        on
          ? "border-genie-secondary/35 text-genie-secondary data-[state=on]:text-genie-secondary"
          : "border-genie-border text-[#64748B] bg-white"
      }`}
    >
      {on && <Check className="w-3 h-3" strokeWidth={2} />}
      {label}
    </Toggle>
  );
};
